import type { GGCoinClient } from "@developerz.ai/ggcoin-sdk";
import { type Accessor, createContext, type ParentProps, useContext } from "solid-js";
import { AuthContextMissingError } from "../errors/index.ts";
import { buildClient } from "../sdk/client.ts";
import { type PersistedSession, SessionStore } from "../sdk/storage.ts";
import { createSessionController } from "./sessionController.ts";

interface AuthContextValue {
  /** Live `GGCoinClient`; its token is in sync with `session()?.jwt`. */
  client: GGCoinClient;
  /** Reactive persisted session accessor, or `null` when signed out. */
  session: Accessor<PersistedSession | null>;
  /** Set after a successful login or token rehydration. */
  setSession(next: PersistedSession): void;
  /** Drop the local session (use `client.logout()` to revoke server-side). */
  clearSession(): void;
}

const AuthContext = createContext<AuthContextValue>();

/**
 * Hydrate the SDK client + session on first mount. The session lives in
 * `localStorage`; later reads come from the signal so the UI tracks token
 * changes (login, logout, expiry).
 */
export function AuthProvider(props: ParentProps) {
  const store = new SessionStore();
  // Build the client untokened, then let the controller seed the token from the
  // hydrated session so the client and the signal start in sync.
  const client = buildClient(null);
  const { session, setSession, clearSession } = createSessionController(store, client);

  return (
    <AuthContext.Provider value={{ client, session, setSession, clearSession }}>
      {props.children}
    </AuthContext.Provider>
  );
}

/** Read the live auth context. Throws if mounted outside an `AuthProvider`. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new AuthContextMissingError();
  return ctx;
}
